import { z } from 'zod';
import { authFetch } from '@os/auth-client';
import { auth } from '../app/auth';

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api/v1';

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export const apiUrl = (path: string) => `${BASE}${path}`;

async function toApiError(res: Response): Promise<ApiError> {
  const json = (await res.json().catch(() => null)) as { error?: { code?: string; message?: string } } | null;
  return new ApiError(res.status, json?.error?.code ?? 'http_error', json?.error?.message ?? `Request failed (${res.status})`);
}

/** Typed JSON call: bearer token + refresh-on-401 from `@os/auth-client`, zod parse of the response (§52). */
export async function api<S extends z.ZodTypeAny>(path: string, opts: { method?: string; body?: unknown; schema?: S; signal?: AbortSignal } = {}): Promise<z.infer<S>> {
  const res = await authFetch(auth, apiUrl(path), {
    method: opts.method ?? 'GET',
    headers: opts.body !== undefined ? { 'Content-Type': 'application/json' } : {},
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });
  if (res.status === 204) return undefined as z.infer<S>;
  if (!res.ok) throw await toApiError(res);
  const json = await res.json().catch(() => null);
  return opts.schema ? opts.schema.parse(json) : (json as z.infer<S>);
}

/** Multipart call for the upload endpoint. */
export async function apiForm<S extends z.ZodTypeAny>(path: string, form: FormData, opts: { method?: string; schema?: S } = {}): Promise<z.infer<S>> {
  const res = await authFetch(auth, apiUrl(path), { method: opts.method ?? 'POST', body: form });
  if (res.status === 204) return undefined as z.infer<S>;
  if (!res.ok) throw await toApiError(res);
  const json = await res.json().catch(() => null);
  return opts.schema ? opts.schema.parse(json) : (json as z.infer<S>);
}

/** Raw response, for binary endpoints. */
export async function apiRaw(path: string, init: RequestInit = {}): Promise<Response> {
  const res = await authFetch(auth, apiUrl(path), init);
  if (!res.ok) throw await toApiError(res);
  return res;
}

/**
 * Fetch image bytes with the bearer token and hand back an object URL. A plain `<img src>` pointing at the API would
 * arrive unauthenticated and be refused. The caller revokes the URL.
 */
export async function apiBlobUrl(path: string): Promise<string> {
  const res = await apiRaw(path);
  return URL.createObjectURL(await res.blob());
}
